import { Link } from 'react-router-dom';
import { MessageCircle, Banknote, ShieldCheck, Clock, Heart, Stethoscope } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

const WHATSAPP_PRICING_URL =
  "https://wa.me/27828951916?text=Hi%2C%20I%27d%20like%20to%20get%20a%20price%20quote%20from%20K%26K%20Medical%20and%20Dental.";

export const PriceList = () => {
  return (
    <div className="min-h-screen" data-testid="price-list-page">
      <SEO
        title="Get a Price Quote"
        description="K&K Medical and Dental offers transparent, personalised pricing. Contact us on WhatsApp for current rates on GP consultations, dental services, surgical procedures, and healthcare packages."
        keywords="medical prices Umzinto, dental prices Scottburgh, consultation quote KZN, GP prices KZN, healthcare quote South Africa"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-800 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-semibold mb-6 backdrop-blur-sm">
            <Banknote className="w-4 h-4" />
            Personalised Pricing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4">
            Get a Price Quote
          </h1>
          <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
            We've moved to personalised pricing — because everyone's health needs are different. Send us a quick WhatsApp message and we'll send you a quote for exactly what you need.
          </p>
        </div>
      </section>

      {/* CTA Card */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
            <div className="bg-gradient-to-r from-teal-500 to-emerald-600 p-8 text-white text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4">
                <MessageCircle className="w-8 h-8" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">How It Works</h2>
              <p className="text-white/90">Get a quote in under a minute</p>
            </div>
            <CardContent className="p-8 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 font-bold flex items-center justify-center flex-shrink-0">1</div>
                <div>
                  <p className="font-semibold text-slate-900 mb-1">Tap the button below</p>
                  <p className="text-slate-600 text-sm">Opens a chat with our team on WhatsApp with a pre-filled message.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 font-bold flex items-center justify-center flex-shrink-0">2</div>
                <div>
                  <p className="font-semibold text-slate-900 mb-1">Tell us what you need</p>
                  <p className="text-slate-600 text-sm">GP consultation, dental service, surgical procedure, healthcare package — anything.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 font-bold flex items-center justify-center flex-shrink-0">3</div>
                <div>
                  <p className="font-semibold text-slate-900 mb-1">Get your quote instantly</p>
                  <p className="text-slate-600 text-sm">Our team replies during operating hours with current rates and next steps.</p>
                </div>
              </div>

              <Button
                asChild
                className="w-full bg-gradient-to-r from-teal-500 to-emerald-600 hover:opacity-90 text-white rounded-full py-6 text-lg shadow-lg mt-2"
                data-testid="get-quote-whatsapp-btn"
              >
                <a href={WHATSAPP_PRICING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Get a Quote on WhatsApp
                </a>
              </Button>

              <p className="text-center text-xs text-slate-500">
                WhatsApp: <a href={WHATSAPP_PRICING_URL} className="text-teal-600 underline">082 895 1916</a> · Email: <a href="mailto:info@kkmedical.co.za" className="text-teal-600 underline">info@kkmedical.co.za</a>
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why we do this */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Why We've Moved to Personalised Pricing</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Heart,
                title: 'Tailored to you',
                body: 'Every patient is different. We quote based on your specific needs, not a one-size-fits-all list.',
              },
              {
                icon: ShieldCheck,
                title: 'Always up to date',
                body: "You'll always receive the current rate. No stale prices on the website that may be out of date.",
              },
              {
                icon: Clock,
                title: 'Faster responses',
                body: 'WhatsApp replies are typically within minutes during operating hours — much faster than email.',
              },
            ].map((item) => (
              <Card key={item.title} className="border-0 shadow-sm hover:shadow-md transition-shadow rounded-2xl">
                <CardContent className="p-6">
                  <div className="bg-teal-100 rounded-xl w-12 h-12 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-teal-500/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
            <Stethoscope className="w-8 h-8 text-teal-400" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Need something specific?</h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Browse our full range of services first, then send us a message — we'll take it from there.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 py-6 text-lg" data-testid="view-services-btn">
              <Link to="/services">View Our Services</Link>
            </Button>
            <Button asChild variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg" data-testid="view-packages-btn">
              <Link to="/packages">Healthcare Packages</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
